import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ReasonIcons, emotions_color, emotions_heads, mainReasonList } from '../../constants/Constants'
import ExpandableText from './ExpandableText'
import ArchiveMedia from './ArchiveMedia'

// Emotion colours range from deep red to pale green, so the label colour is picked by
// contrast rather than hard-coded per emotion.
function readableTextOn(hex) {
    const value = /^#?([0-9a-f]{6})$/i.exec(hex || '')
    if (!value) return '#413F5E'
    const int = parseInt(value[1], 16)
    const [r, g, b] = [(int >> 16) & 255, (int >> 8) & 255, int & 255]
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.62 ? '#413F5E' : '#FFFFFF'
}

// Check-ins have stored reasons in different casings over the years ("social media",
// "Social media"). Match case-insensitively so every reason gets its canonical,
// capitalised label and its icon.
const REASON_LABELS = new Map(mainReasonList.map((reason) => [reason.toLowerCase(), reason]))

function reasonInfo(reason) {
    const key = `${reason}`.trim().toLowerCase()
    const label = REASON_LABELS.get(key) || key.charAt(0).toUpperCase() + key.slice(1)
    return { label, icon: ReasonIcons(key, 16) }
}

/** Shared heading + description + media + link block for content-backed cards. */
function ContentIntro({ content, openImageBig }) {
    const { t } = useTranslation()
    if (!content) return null
    return (
        <>
            <ArchiveMedia files={content.files} title={content.title} openImageBig={openImageBig} />
            {!!content.title && <h3 className="archive-title">{content.title}</h3>}
            <ExpandableText text={content.message || content.description} lines={2} className="archive-description" />
            {!!content.url && (
                <a href={content.url} target="_blank" rel="noreferrer" className="archive-link">
                    {t('openLink')}
                </a>
            )}
        </>
    )
}

/**
 * Who is looking: 'self' (a user's own archive) or 'staff' (a teacher viewing a student).
 * Staff see "Answer" rather than "Your answer", and journal words are withheld by the API.
 */
export const ArchiveViewerContext = React.createContext('self')

export function useAnswerLabel() {
    const { t } = useTranslation()
    const viewer = useContext(ArchiveViewerContext)
    return (count = 1) => {
        if (viewer === 'staff') return count > 1 ? t('archiveAnswers') : t('archiveAnswer')
        return count > 1 ? t('archiveYourAnswers') : t('archiveYourAnswer')
    }
}

/** Full-width button at the foot of a card that opens its read-only results modal. */
export function OpenDetailButton({ onClick, children }) {
    return (
        <button type="button" className="archive-more-button" onClick={onClick} aria-haspopup="dialog">
            {children}
            <svg className="archive-open-arrow" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                <path d="M4.5 3l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </button>
    )
}

export function AnswerLabel({ children }) {
    return <p className="archive-answer-label">{children}</p>
}

export function MoodBody({ response, time }) {
    const { t } = useTranslation()
    const emotion = (response.emotion || '').toLowerCase()
    const color = emotions_color[emotion] || '#BEBECE'
    const head = emotions_heads[emotion] || emotions_heads.default
    const reasons = (response.reasonsList || []).filter(Boolean)

    return (
        <div className="archive-mood">
            <div className="archive-mood-row">
                <span className="archive-mood-face" style={{ '--mood-color': color }}>
                    <img src={head} alt="" width={50} height={50} />
                </span>
                <div className="archive-mood-copy">
                    <p className="archive-mood-title">{t('archiveMoodTitle')}</p>
                    {!!time && <p className="archive-mood-time">{time}</p>}
                </div>
                <span className="archive-mood-pill" style={{ backgroundColor: color, color: readableTextOn(color) }}>
                    {t(emotion)}
                </span>
            </div>
            {reasons.length > 0 && (
                <ul className="archive-reasons" aria-label={t('archiveReasons')}>
                    {reasons.map((reason) => {
                        const { label, icon } = reasonInfo(reason)
                        return (
                            <li key={reason} className="archive-reason">
                                {!!icon && <span className="archive-reason-icon">{icon}</span>}
                                {t(label)}
                            </li>
                        )
                    })}
                </ul>
            )}
            {!!response.message && (
                <div className="archive-quote">
                    <AnswerLabel>{t('comment')}</AnswerLabel>
                    <ExpandableText text={response.message} lines={4} className="archive-quote-text" />
                </div>
            )}
        </div>
    )
}

/** A read-only answer option, styled exactly like a selected/unselected option while answering. */
export function ChosenOption({ selected = true, children }) {
    const { t } = useTranslation()
    return (
        <li className={`archive-option${selected ? ' is-selected' : ''}`}>
            <span className="archive-option-text">{children}</span>
            {selected && <span className="archive-sr-only">({t('archiveSelected')})</span>}
        </li>
    )
}

export function PollBody({ content, response, openImageBig }) {
    const answerLabel = useAnswerLabel()
    const chosen = response.answers || []
    // Fall back to the saved answers if the poll's option list is missing on old content.
    const options = content?.options?.length ? content.options : chosen

    return (
        <>
            <ContentIntro content={content} openImageBig={openImageBig} />
            <AnswerLabel>{answerLabel(chosen.length)}</AnswerLabel>
            <ul className="archive-options">
                {options.map((option, index) => (
                    <ChosenOption key={`${option}-${index}`} selected={chosen.includes(option)}>
                        {option}
                    </ChosenOption>
                ))}
            </ul>
        </>
    )
}

export function QuestionBody({ content, response, openImageBig }) {
    const { t } = useTranslation()
    const answerLabel = useAnswerLabel()
    const answer = (response.answers || []).filter(Boolean).join('\n')

    return (
        <>
            <ContentIntro content={content} openImageBig={openImageBig} />
            <div className="archive-quote">
                <AnswerLabel>{answerLabel(1)}</AnswerLabel>
                {answer ? <ExpandableText text={answer} lines={5} className="archive-quote-text" /> : <p className="archive-muted">{t('archiveNotAnswered')}</p>}
            </div>
        </>
    )
}
